import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { contrastTextOnBackground } from '@shared/utils/contrastText';

/** Фон для строки «сброс фильтра» — не путать со статусом из справочника */
const CLEAR_OPTION_BG = 'var(--gray-05, #f3f5f7)';
const CLEAR_OPTION_BG_HEX = '#f3f5f7';
const FALLBACK_STATUS_BG = '#dfe1e6';
const FILTER_EMPTY_FG = 'var(--gray-90, #0e0e0f)';
const FILTER_CHEVRON_FG = 'var(--gray-80, #3c3e41)';

function Chevron() {
  return (
    <svg
      className="status-select__chevron-svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden
    >
      <path fill="currentColor" d="M6 1 10 5H2L6 1Z" />
      <path fill="currentColor" d="M6 11 2 7h8L6 11Z" />
    </svg>
  );
}

/**
 * Кастомный выбор статуса клиента: фон кнопки и пунктов — из справочника (`color`).
 * Список рендерится в portal с position: fixed, чтобы не обрезался в scroll-таблице.
 */
export function ClientStatusSelect({
  value,
  onChange,
  statuses,
  emptyLabel = '—',
  allowEmpty = true,
  disabled = false,
  /** Только отображение цветного бейджа (без шеврона и списка). */
  readOnly = false,
  /** Подпись, если value нет в statuses (например, неактивный статус из API). */
  fallbackName = null,
  className = '',
}) {
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState({ left: 0, width: 0, maxH: 320, top: 0, bottom: undefined });

  const options = useMemo(() => {
    const rows = statuses.map((s) => ({
      key: s.key,
      name: s.name,
      color: s.color?.trim() ? s.color : FALLBACK_STATUS_BG,
    }));
    if (allowEmpty) {
      return [{ key: '', name: emptyLabel, clear: true }, ...rows];
    }
    return rows;
  }, [statuses, allowEmpty, emptyLabel]);

  const selected = useMemo(() => {
    const fromDict = statuses.find((s) => s.key === value);
    if (fromDict) return fromDict;
    if (value && fallbackName) {
      return { key: value, name: fallbackName, color: FALLBACK_STATUS_BG };
    }
    return undefined;
  }, [statuses, value, fallbackName]);

  const updatePosition = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pad = 4;
    const below = window.innerHeight - r.bottom - pad;
    const above = r.top - pad;
    const wantFlip = below < 180 && above > below;
    const maxH = Math.min(400, Math.max(120, wantFlip ? above - pad : below - pad));
    setBox({
      left: r.left,
      width: r.width,
      top: wantFlip ? undefined : r.bottom + pad,
      bottom: wantFlip ? window.innerHeight - r.top + pad : undefined,
      maxH,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (
        rootRef.current?.contains(t) ||
        listRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isInteractive = !disabled && !readOnly;
  const hasSelection = Boolean(value && selected);
  const btnText = hasSelection ? selected.name : emptyLabel;
  const btnClass = [
    'status-select__btn',
    !hasSelection && allowEmpty ? 'status-select__btn--clear' : '',
    readOnly ? 'status-select__btn--readonly' : '',
  ]
    .filter(Boolean)
    .join(' ');

  let btnStyle;
  let chevronStyle;
  if (hasSelection) {
    const bg = selected.color?.trim() ? selected.color : FALLBACK_STATUS_BG;
    const fg = contrastTextOnBackground(bg);
    btnStyle = { backgroundColor: bg, color: fg };
    chevronStyle = { color: fg };
  } else if (allowEmpty) {
    btnStyle = { color: FILTER_EMPTY_FG };
    chevronStyle = { color: FILTER_CHEVRON_FG };
  } else {
    const bg = selected?.color?.trim() ? selected.color : FALLBACK_STATUS_BG;
    const fg = contrastTextOnBackground(bg);
    btnStyle = { backgroundColor: bg, color: fg };
    chevronStyle = { color: fg };
  }

  const pick = (key) => {
    onChange(key);
    setOpen(false);
  };

  const listStyle = {
    position: 'fixed',
    left: box.left,
    width: box.width,
    zIndex: 10050,
    maxHeight: box.maxH,
    overflowY: 'auto',
    ...(box.top != null ? { top: box.top } : { bottom: box.bottom }),
  };

  return (
    <div
      className={`status-select${readOnly ? ' status-select--readonly' : ''} ${className}`.trim()}
      ref={rootRef}
    >
      <div className="status-select__inner">
        <button
          type="button"
          className={btnClass}
          disabled={disabled || readOnly}
          aria-haspopup={readOnly ? undefined : 'listbox'}
          aria-expanded={readOnly ? undefined : open}
          onClick={() => isInteractive && setOpen((o) => !o)}
          style={btnStyle}
        >
          <span className="status-select__btn-text">{btnText}</span>
        </button>
        {!readOnly ? (
          <span className="status-select__chevron" style={chevronStyle} aria-hidden>
            <Chevron />
          </span>
        ) : null}
      </div>
      {open &&
        isInteractive &&
        box.width > 0 &&
        createPortal(
          <ul
            ref={listRef}
            className="status-select__list"
            role="listbox"
            style={listStyle}
          >
            {options.map((opt) => (
              <li
                key={opt.key === '' ? '__empty' : opt.key}
                role="option"
                aria-selected={value === opt.key}
                className={`status-select__option${opt.clear ? ' status-select__option--clear' : ''}`}
                style={
                  opt.clear
                    ? {
                        backgroundColor: CLEAR_OPTION_BG,
                        color: contrastTextOnBackground(CLEAR_OPTION_BG_HEX),
                      }
                    : {
                        backgroundColor: opt.color,
                        color: contrastTextOnBackground(opt.color),
                      }
                }
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(opt.key)}
              >
                {opt.name}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}
